/**
 * Desktop (`lg` and up) view of the volume-price tiers: a popover under the row's `trigger`
 * with the "The More You Buy, The More You Save" title and `MoreYouBuyMoreYouSaveRows`.
 * Renders nothing when `hasDiscountPrices` is false. Opening and closing the popover writes
 * `moreYouBuyExpanded` back to the row; setting it to true from the row opens the popover
 * when the product has an `id`.
 */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Icon } from "@ryerson/frontend.assets";
import { DiscountPriceModel } from "@ryerson/frontend.common";
import { Flex } from "@ryerson/frontend.layout";
import { Popover, PopperExposedHandlers } from "@ryerson/frontend.modal";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import React from "react";
import { MoreYouBuyMoreYouSaveChildProps } from "./MoreYouBuyMoreYouSave";
import MoreYouBuyMoreYouSaveRows from "./MoreYouBuyMoreYouSaveRows";

const MoreYouBuySection = styled(Flex)`
	width: 350px;
	border-radius: 2px;
	box-shadow:
		-7px -11px 19px 0px rgba(0, 0, 0, 0.07),
		0px 22.3363px 17.869px rgba(0, 0, 0, 0.0417275),
		0px 6.6501px 5.32008px rgba(0, 0, 0, 0.0282725);

	${(props: any) => {
		const { white } = props;
		return css`
			background: ${white};
		`;
	}};
`;

const Divider = styled.div`
	${(props: any) => {
		const { color, dir } = props;
		return dir === "vertical"
			? css`
					border-left: 1px solid ${color};
				`
			: css`
					border-top: 1px solid ${color};
				`;
	}};
	opacity: 0.1;
`;

/** Desktop tier popover; rendered by `MoreYouBuyMoreYouSave.tsx` from the `lg` breakpoint up. */
const MoreYouBuyMoreYouSaveDesktop: React.FC<MoreYouBuyMoreYouSaveChildProps> = ({
	trigger,
	offset,
	moreYouBuyExpanded,
	setMoreYouBuyExpanded,
	content,
	productDetail,
	quantityUom,
	priceUom,
	filteredDiscountedPrice,
	hasDiscountPrices,
	handleOnToggle,
	rowIndex,
}) => {
	const { theme } = useTheme();
	const { lightGray, white, secondaryBrand } = theme.colors.primary;

	const onToggle = (toggle: boolean) => {
		!toggle && popperRef.current?.close();
		handleOnToggle && handleOnToggle(rowIndex || 0);
	};
	React.useEffect(() => {
		!!moreYouBuyExpanded && productDetail?.id && popperRef.current?.open();
	}, [moreYouBuyExpanded]);

	const popperRef = React.useRef<PopperExposedHandlers | null>(null);

	return (
		<>
			{hasDiscountPrices && (
				<Flex direction="column" justifyContent="flex-end">
					<Popover
						ref={popperRef}
						trigger={trigger}
						offset={[0, parseInt(offset) || 0]}
						onOpen={() => {
							setMoreYouBuyExpanded(true);
						}}
						onClose={() => {
							setMoreYouBuyExpanded(false);
						}}
						alignment="bottom"
						dataTestId="more-you-buy-more-you-save-popover"
					>
						<MoreYouBuySection direction="column" {...{ white }}>
							<Flex
								justifyContent="space-between"
								alignItems="center"
								css={{ margin: "10px" }}
							>
								<Typography
									size="md"
									color={secondaryBrand}
									css={{ fontWeight: 500, width: "90%" }}
								>
									{`${content.theMoreYouBuyHeader}${", "}${
										content.theMoreYouSaveHeader
									}`}
								</Typography>
								<Icon icon="close" circled={true} onClick={() => onToggle(false)} />
							</Flex>
							<MoreYouBuyMoreYouSaveRows
								content={content}
								productDetail={productDetail}
								quantityUom={quantityUom}
								priceUom={priceUom}
								filteredDiscountedPrice={filteredDiscountedPrice}
							/>
						</MoreYouBuySection>
					</Popover>
					<Divider color={lightGray} />
				</Flex>
			)}
		</>
	);
};

export default MoreYouBuyMoreYouSaveDesktop;
